// url-credentials.ts — masks URL logins (`scheme://user:pass@host`) in
// renderer telemetry before it leaves for PostHog or Sentry. A failed camera
// add echoes the stream URL the user composed, login included.
//
// The pattern and marker are the canonical scrubber's (urlCredRe in
// api/core.mailroom-lambda-utils/sanitizer.go), as already ported to
// TypeScript in studio/electron/src/main/loggy/sanitizer.ts, whose length
// bounds keep this backtracking regex engine linear on crafted input. Neither
// module can be imported into the renderer; url-credentials.test.ts pins this
// copy to the loggy one.

const urlCredRe = /([a-zA-Z][a-zA-Z0-9+.-]{0,32}:\/\/)([^@\s]{1,512})@/g;
const URL_CRED_REPLACEMENT = '$1[USER_PASS_REDACTED]@';

// Telemetry payloads nest far less deeply; the bound keeps a cyclic value from
// recursing forever. Objects nested deeper are passed through unscrubbed.
const MAX_DEPTH = 8;

/**
 * Mask the login of every URL in a string, keeping scheme, host, port and path
 * so the text still names the camera.
 *
 * @param input - Free text that may embed URLs.
 * @returns The text with each `user:pass@` replaced by `[USER_PASS_REDACTED]@`.
 */
export function scrubUrlCredentials(input: string): string {
  return input.replace(urlCredRe, URL_CRED_REPLACEMENT);
}

/**
 * Mask URL logins in every string inside plain objects and arrays (a PostHog
 * event's properties, a Sentry event). Other objects pass through as they are.
 *
 * @param value - The payload to scrub.
 * @returns The same reference wherever nothing changed, otherwise a copy.
 */
export function scrubUrlCredentialsDeep<T>(value: T): T {
  return scrubValue(value, 0) as T;
}

function scrubValue(value: unknown, depth: number): unknown {
  if (typeof value === 'string') {
    return scrubUrlCredentials(value);
  }
  if (depth >= MAX_DEPTH || value === null || typeof value !== 'object') {
    return value;
  }

  if (Array.isArray(value)) {
    let changed = false;
    const next = value.map((item: unknown) => {
      const scrubbed = scrubValue(item, depth + 1);
      if (scrubbed !== item) {
        changed = true;
      }
      return scrubbed;
    });
    return changed ? next : value;
  }

  const prototype: unknown = Object.getPrototypeOf(value);
  if (prototype !== Object.prototype && prototype !== null) {
    return value;
  }

  let next: Record<string, unknown> | undefined;
  for (const [key, item] of Object.entries(value)) {
    const scrubbed = scrubValue(item, depth + 1);
    if (scrubbed !== item) {
      next ??= { ...value };
      next[key] = scrubbed;
    }
  }
  return next ?? value;
}
